import { FC, useEffect, useMemo, useState } from "react";
import { TextField } from "@mui/material";
import BasicSelect from "../../components/BasicSelect";
import { useGroupLabels } from "../../hooks/groupLabels";
import { ValidQlikOptions } from "../../domain/report.interface";
import { Program } from "../../domain/beneficiary.interface";
import { useUserStore } from "../../stores/user.store";
import {
	useGetProgramsByIds,
	useGetProviderSitesByOUId,
	useGetProvidersByProgramId,
} from "../../hooks/useBeneficiaries";
import { useSettings } from "../../hooks/useSetting";

export type ReportFiltersProps = {
	programId: string[];
	providerSiteId: string[];
	providerId: string;
	providerNpi: string;
	beneficiary: string;
	beneficiaryCin: string;
};

type Props = {
	filters: ReportFiltersProps;
	onFiltersChange: (newFilters: ReportFiltersProps) => void;
	showBeneficiaryFilters?: boolean;
	validOptions?: ValidQlikOptions;
};

export const ReportFilters: FC<Props> = ({
	filters,
	onFiltersChange,
	showBeneficiaryFilters = true,
	validOptions,
}) => {
	const { userInfo } = useUserStore();
	// const queryClient = useQueryClient();
	const { data: settingsV2 } = useSettings(userInfo?.id);

	const userPrograms =
		userInfo?.programs.map((program) => program.programId) || [];

	const [programsOptions, setProgramsOptions] = useState<
		Array<{ key: string; value: string }>
	>([]);
	const [providerOptions, setProviderOptions] = useState<
		Array<{ key: string; value: string }>
	>([]);

	const [programOuiSelected, setProgramOuiSelected] = useState<string[]>();
	type ProviderSiteWithLabels = {
		key: string;
		id: string;
		name: string;
		value: string;
		groupLabelIds?: string[];
	};

	const [filteredProviderSitesData, setFilteredProviderSitesData] = useState<
		ProviderSiteWithLabels[]
	>([]);
	const [selectedProviderSites, setSelectedProviderSites] = useState<string[]>(
		[],
	);
	const [selectedGroupLabels, setSelectedGroupLabels] = useState<string[]>([]);
	const [selectedProgram, setSelectedProgram] = useState<Program | undefined>();
	const { data: groupLabels = [], isLoading: isLoadingGroupLabels } =
		useGroupLabels();

	// programs
	const { data: programsData, isLoading: isLoadingPrograms } =
		useGetProgramsByIds(userPrograms);

	// provider sites
	const { data: providerSitesData, refetch: refetchProviderSites } =
		useGetProviderSitesByOUId({
			ouIds: programOuiSelected || [],
			queryKey: "sims-beneficiaries-list-provider-sites",
		});

	useEffect(() => {
		if (
			!providerSitesData ||
			!filters.programId ||
			filters.programId.length === 0
		) {
			setFilteredProviderSitesData([]);
			return;
		}
		setFilteredProviderSitesData(providerSitesData);

		// Auto-select all OUs when provider sites are loaded
		const allOuIds = providerSitesData.map((ou) => ou.id);
		setSelectedProviderSites(allOuIds);

		onFiltersChange({ ...filters, providerSiteId: allOuIds });
	}, [filters.programId, providerSitesData]);

	// providers - only fetch when we have valid program and provider site IDs
	const { isLoading: isLoadingProviders, refetch: refetchProviders } =
		useGetProvidersByProgramId(
			filters.programId.length > 0 && filters.programId[0] !== ""
				? filters.programId
				: [],
			filters.providerSiteId.length > 0 ? filters.providerSiteId : [],
			false,
		);

	const onProgramFilterChange = (value: string | string[]) => {
		if (!Array.isArray(value)) {
			const valueArray = value.includes(",") ? value.split(",") : [value];

			setFilteredProviderSitesData([]);

			const allOuIds =
				userInfo?.programs
					.filter((p) => valueArray.includes(p.programId))
					.flatMap((p) =>
						p.orgUnitId.map((o) => (typeof o === "string" ? o : o.orgUnitId)),
					) || [];

			setProgramOuiSelected(allOuIds);
			setProviderOptions([]);

			// Set selected program based on the first selected program ID
			const newSelectedProgram = programsData?.find((p) =>
				valueArray.includes(p._id),
			);
			setSelectedProgram(newSelectedProgram as unknown as Program);

			const newFilters = {
				...filters,
				programId: valueArray,
				providerId: "",
				providerSiteId: allOuIds,
			};
			onFiltersChange(newFilters);
			setSelectedProviderSites(allOuIds);
			setSelectedGroupLabels([]);
		}
	};

	useEffect(() => {
		if (programsData && !isLoadingPrograms && programsData.length > 0) {
			const programOptions = programsData.map((program) => ({
				key: program._id,
				value: program.name,
			}));

			setProgramsOptions(programOptions);

			// Only set initial program if not already set
			if (filters.programId.length === 0 || filters.programId[0] === "") {
				const userProgramsOuis =
					userInfo?.programs
						.filter((p) => p.programId === programsData[0]?._id)
						.flatMap((p) =>
							p.orgUnitId.map((o) => (typeof o === "string" ? o : o.orgUnitId)),
						) || [];

				setProgramOuiSelected(userProgramsOuis);

				// Set initial selected program
				if (programsData[0]) {
					setSelectedProgram(programsData[0] as unknown as Program);
				}

				const newFilters = {
					...filters,
					programId: [programsData[0]?._id],
					providerId: "",
					providerSiteId: [],
				};
				onFiltersChange(newFilters);
				setProviderOptions([]);
			} else {
				// Set selected program based on current filter
				const currentProgram = programsData.find((p) =>
					filters.programId.includes(p._id),
				);
				if (currentProgram) {
					setSelectedProgram(currentProgram as unknown as Program);
				}
			}
		}
	}, [programsData, isLoadingPrograms, filters.programId]);

	useEffect(() => {
		if (
			filters.programId.length > 0 &&
			programOuiSelected &&
			programOuiSelected.length > 0
		) {
			refetchProviderSites();
		}
	}, [filters.programId, programOuiSelected, refetchProviderSites]);

	useEffect(() => {
		if (
			filters.programId.length > 0 &&
			filters.providerSiteId.length > 0 &&
			!isLoadingProviders
		) {
			refetchProviders().then((data) => {
				let filteredProviders = data?.data || [];

				// Filter providers by CM roles
				filteredProviders = filteredProviders.filter((p) =>
					p.roles?.some((r) => selectedProgram?.cmRoles?.includes(r)),
				);

				// Filter providers based on valid Qlik options if available
				if (validOptions?.providerIds) {
					filteredProviders = filteredProviders.filter((provider) =>
						validOptions?.providerIds?.includes(provider.value),
					);
				}

				setProviderOptions(filteredProviders);
			});
		}
	}, [isLoadingProviders, filters, validOptions, selectedProgram]);

	const groupLabelOptions = useMemo(() => {
		const availableLabels = providerSitesData?.flatMap(
			(ou) => ou.groupLabelIds || [],
		);
		return groupLabels
			.filter((l) => availableLabels?.includes(l._id))
			.map((label) => ({
				key: label._id,
				value: label.name,
			}));
	}, [groupLabels, providerSitesData]);

	return (
		<>
			<BasicSelect
				handleChange={onProgramFilterChange}
				data={programsOptions || []}
				value={filters.programId}
				label="Select Program"
				id="report-program"
				placeholder="Select"
				isLoading={isLoadingPrograms}
			/>

			<BasicSelect
				handleChange={(value) => {
					// Always flatten the value to a string[]
					let selected: string[] = [];
					if (Array.isArray(value)) {
						selected = value.flat();
					} else {
						selected = [value];
					}
					// Prevent unselecting all: if user tries to unselect all, re-select all
					if (selected.length === 0 && filteredProviderSitesData.length > 0) {
						selected = filteredProviderSitesData.map((ou) => ou.id);
					}
					setSelectedProviderSites(selected);
					setSelectedGroupLabels([]); // Deselect group labels when OU is selected
					const newFilters = {
						...filters,
						providerSiteId: selected,
						groupLabelIds: [],
						providerId: "",
					};
					onFiltersChange(newFilters);
					setProviderOptions([]);
				}}
				data={(filteredProviderSitesData || []).map((ou) => ({
					key: ou.id || ou.key,
					value: ou.name || ou.value,
				}))}
				value={selectedProviderSites}
				label="Select OU"
				id="report-ou"
				multiple
				isLoading={false}
			/>

			<BasicSelect
				handleChange={(value) => {
					const selectedLabels = Array.isArray(value) ? value : [value];
					setSelectedGroupLabels(selectedLabels);
					let selectedOuIds: string[];
					if (selectedLabels.length === 0) {
						// No labels selected: select all OUs
						selectedOuIds = filteredProviderSitesData.map((ou) => ou.id);
					} else {
						// Find OUs with any of the selected group labels
						const matchingOUs = (filteredProviderSitesData || []).filter(
							(ou) =>
								Array.isArray(ou.groupLabelIds) &&
								ou.groupLabelIds.some((labelId) =>
									selectedLabels.includes(labelId),
								),
						);
						selectedOuIds = matchingOUs.map((ou) => ou.id);
					}
					setSelectedProviderSites(selectedOuIds);
					const newFilters = {
						...filters,
						groupLabelIds: selectedLabels,
						providerSiteId: selectedOuIds,
						providerId: "",
					};
					onFiltersChange(newFilters);
					setProviderOptions([]);
				}}
				data={groupLabelOptions}
				value={selectedGroupLabels}
				label="Select Group Label"
				id="report-group-label"
				multiple
				isLoading={isLoadingGroupLabels}
			/>

			<BasicSelect
				handleChange={(value) => {
					const newFilters = {
						...filters,
						providerId: value as string,
					};
					onFiltersChange(newFilters);
				}}
				data={providerOptions}
				value={filters.providerId}
				disabled={
					filters.programId.includes("all") ||
					providerOptions.length === 0 ||
					isLoadingProviders
				}
				label="Select Provider"
				id="report-provider"
				placeholder="Select"
			/>
			<TextField
				label="Provider NPI"
				value={filters.providerNpi}
				onChange={(e) =>
					onFiltersChange({
						...filters,
						providerNpi: e.target.value,
					})
				}
			/>
			{showBeneficiaryFilters && (
				<>
					{settingsV2?.displayBeneficiaryName ? (
						<TextField
							label="Beneficiary"
							value={filters.beneficiary}
							onChange={(e) =>
								onFiltersChange({
									...filters,
									beneficiary: e.target.value,
								})
							}
						/>
					) : (
						<></>
					)}
					<TextField
						label="Beneficiary CIN"
						value={filters.beneficiaryCin}
						onChange={(e) =>
							onFiltersChange({
								...filters,
								beneficiaryCin: e.target.value,
							})
						}
					/>
				</>
			)}
		</>
	);
};

export default ReportFilters;
